import { Link, useHref } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useStoreActions, useStoreState } from 'easy-peasy';
import {
  downloadDataSelector,
  getCandidateCommitmentLetterSelector,
  getCandidateResignationLetterSelector,
  setDownloadDataSelector,
} from '@model/Candidate/Candidate.selectors';
import { useDownloadFile } from '@utils/hooks/use-download-file';
import {
  CANDIDATES_SEGMENT,
  SUCCESS_FEE_SEGMENT,
} from '@app/constants/navigation.constants';
import { memo } from 'react';
import { selectorIsCompensationVisible } from '@model/showcase-table';
import classNames from 'classnames';
import { useUserPackageVisibility } from '@components/ShareLinkModal/_hooks/use-user-package-visibility';
import {
  Action,
  ACTION_VARIANTS,
  Card,
  Icon,
  ICON_NAMES,
} from '@reverse-hr/design-system';

export const ClosingHiredCard = memo(({ closing, jobId }) => {
  const { t } = useTranslation();
  const candidateHref = `/jobs/${jobId}/candidates/${closing.id}`;
  const successFeeHref = `/jobs/${jobId}/${CANDIDATES_SEGMENT}/${closing.id}/${SUCCESS_FEE_SEGMENT}`;

  const downloadData = useStoreState(downloadDataSelector);
  const setDownloadData = useStoreActions(setDownloadDataSelector);
  const isCompensationVisible = useStoreState(selectorIsCompensationVisible);
  const userPackageVisibility = useUserPackageVisibility();

  useDownloadFile({
    url: downloadData?.url,
    filename: downloadData?.filename,
    cb: () => setDownloadData(null),
  });

  const getResignationLetter = useStoreActions(
    getCandidateResignationLetterSelector,
  );

  const getCommitmentLetter = useStoreActions(
    getCandidateCommitmentLetterSelector,
  );

  const handleDownload = async handleDownloadThunk => {
    return await handleDownloadThunk({
      candidateId: closing.id,
      jobId,
    });
  };

  const hasCommitmentLetter = !!closing.offer.hasCommitmentLetter;
  const hasResignationLetter = !!closing.offer.hasResignationLetter;
  const hasSuccessFeeRecap = !!closing.offer.successFeeRecap;
  const areLettersVisible =
    isCompensationVisible && userPackageVisibility.isVisible;

  const wrapperClassNames = classNames('c-hiring-card', {
    'c-hiring-card--padded': !areLettersVisible,
  });

  return (
    <div>
      <Card className={wrapperClassNames}>
        <div className="c-hiring-card__head">
          <p className="c-hiring-card__title">
            {t('common.formatting.fullName', {
              firstName: closing.firstName,
              lastName: closing.lastName,
            })}
          </p>

          <Link to={candidateHref}>
            <Icon name={ICON_NAMES.ID_CARD} />
          </Link>
        </div>

        {areLettersVisible && (
          <div className="c-hiring-card__letters">
            {hasCommitmentLetter && (
              <LetterButton
                label={t('closing.links.commitment_letter')}
                onClick={() => handleDownload(getCommitmentLetter)}
              />
            )}

            {hasResignationLetter && (
              <LetterButton
                label={t('closing.links.resignation_letter')}
                onClick={() => handleDownload(getResignationLetter)}
              />
            )}
          </div>
        )}

        {isCompensationVisible && (
          <div className="c-hiring-card__actions">
            {hasSuccessFeeRecap && (
              <Link
                className="c-hiring-card__success-fee-link"
                to={successFeeHref}
              >
                <Action
                  rightIcon={ICON_NAMES.CHEVRON_BIG_RIGHT}
                  variant={ACTION_VARIANTS.OUTLINED}
                >
                  {t('closing.links.prospect')}
                </Action>
              </Link>
            )}
          </div>
        )}
      </Card>
    </div>
  );
});

const LetterButton = ({ label, onClick }) => (
  <button
    className="c-hiring-card__letter-button"
    onClick={onClick}
    type="button"
  >
    <Icon name={ICON_NAMES.DOWNLOAD} />
    {label}
  </button>
);
